/**
 * Print Depot — motion for the Shopify Forms newsletter popup
 *
 * The popup is rendered by the Forms app embed inside an open shadow root on
 * <shopify-forms-embed>, so theme CSS cannot reach it and the app offers no
 * animation settings. This script appends a stylesheet to that shadow root and
 * gives the popup the same entrance language as the pd-* sections: opacity and
 * a short rise run as separate animations on the site's fade and entrance
 * curves (see pd-base.css), with the card's contents staggered behind it.
 *
 * Leaving is animated too. Shadow-root capture listeners sit above the app's
 * own handlers, so a click on the close button, the backdrop or the teaser is
 * held back while the exit plays, then replayed so the app does its normal
 * close or open. The app's class names are build hashes (_formContainer_1q1d2_30),
 * so every selector matches on the stable prefix or on data attributes.
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const STYLE_ID = 'pd-forms-motion';
  const EXIT_MS = 380;
  const TEASER_EXIT_MS = 240;

  const CARD = '[class*="_formContainer_"]';
  const BACKDROP = '[class*="_overlayBackground_"]';
  const CLOSE = '[class*="_formCloseButton_"]';
  const TEASER = '[data-testid="form-teaser"]';
  const CONTENT = '[class*="_gridItemContent_"]';

  const CSS = `
    :host {
      --pdf-ease-entrance: var(--pd-ease-entrance, cubic-bezier(0.25, 1, 0.5, 1));
      --pdf-ease-fade: var(--pd-ease-fade, cubic-bezier(0.33, 0, 0.2, 1));
      --pdf-ease-exit: cubic-bezier(0.4, 0, 0.7, 0.2);
      --pdf-ease: var(--pd-ease, cubic-bezier(0.22, 0.61, 0.36, 1));
    }

    @keyframes pdf-fade-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes pdf-fade-out { from { opacity: 1; } to { opacity: 0; } }
    /* The app keeps the backdrop at opacity 0 / hidden and reveals it only by
       animation, so these must end on its configured opacity and hold it. */
    @keyframes pdf-backdrop-in {
      from { opacity: 0; visibility: visible; }
      to { opacity: var(--forms-overlay-background-opacity, 0.6); visibility: visible; }
    }
    @keyframes pdf-backdrop-out {
      from { opacity: var(--forms-overlay-background-opacity, 0.6); visibility: visible; }
      to { opacity: 0; visibility: visible; }
    }
    @keyframes pdf-card-in {
      from { transform: translate3d(0, 28px, 0) scale(0.965); }
      to { transform: none; }
    }
    @keyframes pdf-rise-in {
      from { transform: translate3d(0, 14px, 0); }
      to { transform: none; }
    }
    @keyframes pdf-card-out {
      from { opacity: 1; transform: none; }
      to { opacity: 0; transform: translate3d(0, 18px, 0) scale(0.97); }
    }
    @keyframes pdf-teaser-in {
      from { transform: translate3d(0, 24px, 0) scale(0.94); }
      to { transform: none; }
    }
    @keyframes pdf-teaser-shake {
      0%, 14%, 100% { transform: none; }
      2% { transform: translate3d(-4px, 0, 0) rotate(-2.5deg); }
      4% { transform: translate3d(4px, 0, 0) rotate(2.5deg); }
      6% { transform: translate3d(-3px, 0, 0) rotate(-1.75deg); }
      8% { transform: translate3d(3px, 0, 0) rotate(1.75deg); }
      10% { transform: translate3d(-1.5px, 0, 0) rotate(-0.75deg); }
      12% { transform: translate3d(1px, 0, 0) rotate(0.5deg); }
    }
    @keyframes pdf-teaser-out {
      from { opacity: 1; transform: none; }
      to { opacity: 0; transform: translate3d(0, 16px, 0) scale(0.94); }
    }

    /* The app fades its whole router in; each piece below brings its own. */
    [data-testid="app-router"] { animation: none !important; }

    ${BACKDROP} {
      animation: pdf-backdrop-in 520ms var(--pdf-ease-fade) both !important;
    }

    ${CARD} {
      transform-origin: 50% 65%;
      animation:
        pdf-fade-in 560ms var(--pdf-ease-fade) 80ms backwards,
        pdf-card-in 950ms var(--pdf-ease-entrance) 80ms backwards !important;
    }

    /* Header, form and the success message rise in behind the card. */
    ${CONTENT} > * {
      animation:
        pdf-fade-in 700ms var(--pdf-ease-fade) backwards,
        pdf-rise-in 1000ms var(--pdf-ease-entrance) backwards !important;
      animation-delay: 240ms, 240ms !important;
    }
    ${CONTENT} > :nth-child(2) { animation-delay: 330ms, 330ms !important; }
    ${CONTENT} > :nth-child(3) { animation-delay: 420ms, 420ms !important; }
    ${CONTENT} > :nth-child(n + 4) { animation-delay: 510ms, 510ms !important; }

    /* The teaser wiggles to catch the eye: a short decaying shake at the start
       of every 6s loop, first after it has settled in, paused on hover. The
       hover lift uses the separate translate property so it never fights the
       shake's transform. */
    ${TEASER} {
      transform-origin: 50% 50%;
      transition: translate 250ms var(--pdf-ease), box-shadow 250ms var(--pdf-ease);
      animation:
        pdf-fade-in 520ms var(--pdf-ease-fade) 120ms backwards,
        pdf-teaser-in 850ms var(--pdf-ease-entrance) 120ms backwards,
        pdf-teaser-shake 6s ease-in-out 2.2s infinite !important;
    }
    ${TEASER}:hover,
    ${TEASER}:focus-visible {
      translate: 0 -3px;
      box-shadow: 0 12px 28px rgb(15 18 24 / 0.18);
      animation-play-state: running, running, paused !important;
    }

    /* Exit, held by the script until it finishes. */
    [data-pd-leaving] ${CARD} {
      animation: pdf-card-out ${EXIT_MS}ms var(--pdf-ease-exit) forwards !important;
    }
    [data-pd-leaving] ${CARD} * { animation: none !important; }
    [data-pd-leaving] ${BACKDROP} {
      animation: pdf-backdrop-out ${EXIT_MS}ms var(--pdf-ease-fade) forwards !important;
    }
    [data-pd-leaving] ${TEASER} {
      animation: pdf-teaser-out ${TEASER_EXIT_MS}ms var(--pdf-ease-exit) forwards !important;
    }

    @media (prefers-reduced-motion: reduce) {
      ${CARD}, ${CONTENT} > *, ${TEASER} {
        animation: pdf-fade-in 200ms linear backwards !important;
      }
      ${BACKDROP} { animation: pdf-backdrop-in 200ms linear both !important; }
      ${TEASER}:hover { translate: none; }
    }
  `;

  const watchRoot = (sr) => {
    // Some app re-renders replace the shadow root's children; keep the sheet.
    const ensureStyle = () => {
      if (sr.getElementById(STYLE_ID)) return;
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CSS;
      sr.appendChild(style);
    };
    ensureStyle();
    new MutationObserver(ensureStyle).observe(sr, { childList: true });

    if (reduced) return;

    let replaying = false;

    const hold = (event, target, duration) => {
      const root = sr.querySelector('[data-form-root]');
      event.stopImmediatePropagation();
      event.preventDefault();
      if (!root || root.hasAttribute('data-pd-leaving')) return;

      root.setAttribute('data-pd-leaving', '');

      // Drop the exit state only once the app has swapped steps, so the
      // closed card never flashes back at full opacity before it unmounts.
      const release = () => {
        stepWatch.disconnect();
        clearTimeout(fallback);
        root.removeAttribute('data-pd-leaving');
      };
      const stepWatch = new MutationObserver(release);
      stepWatch.observe(root, { attributes: true, attributeFilter: ['data-current-step'] });
      const fallback = setTimeout(release, duration + 800);

      setTimeout(() => {
        replaying = true;
        target.click();
        replaying = false;
      }, duration);
    };

    const actionFor = (node) => {
      const close = node.closest?.(CLOSE);
      if (close) return { target: close, duration: close.closest(TEASER) ? TEASER_EXIT_MS : EXIT_MS };
      const backdrop = node.closest?.(BACKDROP);
      if (backdrop) return { target: backdrop, duration: EXIT_MS };
      const teaser = node.closest?.(TEASER);
      if (teaser) return { target: teaser, duration: TEASER_EXIT_MS };
      return null;
    };

    sr.addEventListener(
      'click',
      (event) => {
        if (replaying) return;
        const action = actionFor(event.target);
        if (action) hold(event, action.target, action.duration);
      },
      true
    );

    sr.addEventListener(
      'keydown',
      (event) => {
        if (replaying) return;
        if (event.key === 'Escape') {
          const close = sr.querySelector(`${CARD} ${CLOSE}`);
          if (close) hold(event, close, EXIT_MS);
        } else if (event.key === 'Enter' || event.key === ' ') {
          const action = actionFor(event.target);
          if (action) hold(event, action.target, action.duration);
        }
      },
      true
    );
  };

  const attached = new WeakSet();

  const attach = (host) => {
    if (attached.has(host)) return;
    // The element can be inserted before it upgrades and attaches its root.
    let tries = 0;
    const wait = () => {
      if (host.shadowRoot) {
        attached.add(host);
        watchRoot(host.shadowRoot);
      } else if (tries++ < 300) {
        requestAnimationFrame(wait);
      }
    };
    wait();
  };

  // The app inserts its host once per page load; stop watching after that.
  const pageWatch = new MutationObserver(() => scan());
  const scan = () => {
    const hosts = document.querySelectorAll('shopify-forms-embed');
    hosts.forEach(attach);
    if (hosts.length) pageWatch.disconnect();
  };

  pageWatch.observe(document.documentElement, { childList: true, subtree: true });
  scan();
})();
